import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './style.css';
import Modal from 'react-modal';

import '@rainbow-me/rainbowkit/styles.css';
import '@relayprotocol/relay-kit-ui/styles.css';
import { 
  RainbowKitProvider, 
  darkTheme
} from '@rainbow-me/rainbowkit';
import { PrivyProvider } from '@privy-io/react-auth';
import { toSolanaWalletConnectors } from '@privy-io/react-auth/solana';
import { WagmiProvider, createConfig } from '@privy-io/wagmi';
import { http } from 'viem';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RelayKitProvider } from '@relayprotocol/relay-kit-ui';
import { convertViemChainToRelayChain, MAINNET_RELAY_API } from '@relayprotocol/relay-sdk';
import { ink, monad } from './utils/chains';
import { registerSW } from 'virtual:pwa-register';
import { mainnet, base, optimism, arbitrum, polygon, bsc, avalanche } from 'wagmi/chains';

// Register PWA Service Worker
const updateSW = registerSW({
  onNeedRefresh() {
    // Non-blocking update check
    console.log('New content available. Updating...');
    updateSW(true);
  },
  onOfflineReady() {
    console.log('App is ready for offline usage.');
  },
});

// Privy App ID - Required environment variable (set in Vercel)
const PRIVY_APP_ID = import.meta.env.VITE_PRIVY_APP_ID;

if (!PRIVY_APP_ID) {
  console.error('VITE_PRIVY_APP_ID environment variable is not set. Authentication will not work.');
}

// EVM chains only (Solana not supported in wagmi)
const evmChains = [ink, monad, mainnet, base, optimism, arbitrum, polygon, bsc, avalanche] as const;
const relayChains = evmChains.map(chain => convertViemChainToRelayChain(chain));

// Create wagmi config using Privy's createConfig (auto-adds Privy wallet connector)
const config = createConfig({
  chains: evmChains as any,
  transports: {
    // Ink uses DRPC for reliable gas estimation (Gelato sponsored RPC fails on standard txns)
    57073: http('https://ink.drpc.org'),
    // Monad RPC
    143: http('https://rpc.monad.xyz'),
    // Default public RPCs for other chains
    1: http(),
    8453: http(),
    10: http(),
    42161: http(),
    137: http(),
    56: http(),
    43114: http(),
  },
});

const queryClient = new QueryClient();

// Set app element for react-modal
Modal.setAppElement('#app');

ReactDOM.createRoot(document.getElementById('app') as HTMLElement).render(
  <React.StrictMode>
    <PrivyProvider
      appId={PRIVY_APP_ID}
      config={{
        // Login methods - users can sign up with email, social, or connect existing wallet
        // Beholder integration is handled via useCrossAppAccounts hook in WalletConnect.tsx
        loginMethods: ['email', 'wallet', 'google', 'twitter', 'discord'],
        // Appearance
        appearance: {
          theme: 'dark',
          accentColor: '#a855f7',
          logo: 'https://mavrk.ink/Mavrk_Logo.png',
          showWalletLoginFirst: false, // Show email/social first for easier onboarding
          walletList: ['detected_wallets', 'metamask', 'coinbase_wallet', 'rainbow', 'wallet_connect', 'phantom', 'solflare'],
          walletChainType: 'ethereum-and-solana', // Enable both EVM and Solana wallets
        },
        // External wallet connectors for Solana
        externalWallets: {
          solana: {
            connectors: toSolanaWalletConnectors(),
          },
        },
        // Embedded wallet config - EVM chains (ZeroDev smart wallets)
        embeddedWallets: {
          ethereum: {
            createOnLogin: 'all-users', // Always create embedded wallets for all users
          },
          solana: {
            createOnLogin: 'all-users',
          },
        },
        // Default chain - Ink
        defaultChain: ink,
        // Supported chains
        supportedChains: [ink, mainnet, base, optimism, arbitrum, polygon, bsc, avalanche, monad],
      }}
    >
      <QueryClientProvider client={queryClient}>
        <RelayKitProvider
          options={{
            appName: 'Mavrk.Ink',
            baseApiUrl: MAINNET_RELAY_API,
            source: 'mavrk.ink',
            chains: relayChains,
            themeScheme: 'dark',
          }}
          theme={{
            font: 'Inter, sans-serif',
            primaryColor: '#a855f7',
            focusColor: '#d8b4fe',
            subtleBackgroundColor: '#000000',
            subtleBorderColor: '#333333',
            text: {
              default: '#ffffff',
              subtle: '#9ca3af',
              error: '#ef4444',
              success: '#10b981',
            },
            buttons: {
              primary: {
                color: '#ffffff',
                background: '#581c87',
                hover: {
                  color: '#ffffff',
                  background: '#6b21a8',
                }
              },
              secondary: {
                color: '#ffffff',
                background: 'rgba(88, 28, 135, 0.3)',
                hover: {
                  color: '#ffffff',
                  background: 'rgba(88, 28, 135, 0.5)',
                }
              }
            },
            input: {
              background: '#000000',
              borderRadius: '12px',
              color: '#ffffff',
            },
            dropdown: {
              background: '#000000',
              borderRadius: '12px',
              border: '1px solid #333333',
            },
            widget: {
              background: '#000000',
              borderRadius: '20px',
              border: 'transparent',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              card: {
                background: '#000000',
                borderRadius: '12px',
                border: '2px solid #333333',
              },
              selector: {
                background: 'rgba(168, 85, 247, 0.15)',
                hover: {
                  background: 'rgba(168, 85, 247, 0.25)',
                }
              },
              swapCurrencyButtonBorderColor: 'rgba(168, 85, 247, 0.3)',
              swapCurrencyButtonBorderWidth: '2px',
              swapCurrencyButtonBorderRadius: '8px',
            },
            modal: {
              background: '#000000',
              border: '1px solid #333333',
              borderRadius: '20px',
            }
          }}
        >
          <WagmiProvider config={config}>
            <RainbowKitProvider
              theme={darkTheme({
                accentColor: '#a855f7',
                accentColorForeground: 'white',
                borderRadius: 'medium',
                fontStack: 'system',
                overlayBlur: 'small',
              })}
            >
              <App />
            </RainbowKitProvider>
          </WagmiProvider>
        </RelayKitProvider>
      </QueryClientProvider>
    </PrivyProvider>
  </React.StrictMode>
);
